import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { CompaniesService, Company } from 'src/app/core/companies.service';
import { LongRange, LongRangeService } from 'src/app/core/long-range.service';
import { StoresAllowedStepsDialogComponent } from 'src/app/stores/stores-allowed-steps-dialog/stores-allowed-steps-dialog.component';
import { UtilsService } from 'src/app/core/utils.service';
import { DialogService } from 'src/app/core/dialog.service';
import { ErrorService } from 'src/app/core/error.service';
import { ERROR_EMPTY_LONG_RANGES_MESSAGE } from 'src/app/shared/constants/translations';

@Component({
  selector: 'app-companies-allowed-steps-dialog',
  templateUrl: './companies-allowed-steps-dialog.component.html',
  styleUrls: ['./companies-allowed-steps-dialog.component.scss'],
})
export class CompaniesAllowedStepsDialogComponent {
  company?: Company;

  default_long_ranges?: LongRange[];
  company_long_ranges?: LongRange[];

  is_list_long_ranges_running: boolean = true;
  is_update_company_running: boolean = false;

  displayed_columns: string[] = ['amount_range', 'allowed_steps'];
  company_business_name?: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private dialog_ref: MatDialogRef<StoresAllowedStepsDialogComponent>,
    private long_range_service: LongRangeService,
    private error_service: ErrorService,
    private companies_service: CompaniesService,
    private utils_service: UtilsService,
    private dialog_service: DialogService,
  ) {
    this.company = data.company;
  }

  ngOnInit(): void {
    this.readCompany(this.company?.company_id as string);
  }

  onRefresh(): void {
    this.readCompany(this.company?.company_id as string);
  }

  onCloseClick(): void {
    this.dialog_ref.close();
  }

  readCompany(company_id: string): void {
    this.is_list_long_ranges_running = true;
    this.companies_service.readCompany(company_id).subscribe({
      next: response => {
        this.company = response.data;
        this.company_business_name = this.company?.business_name;
        this.company_long_ranges = this.orderLongRange(this.company?.long_ranges || []);
        this.listLongRanges();
      },
      error: error => {
        this.error_service.processGenericApiError(error);
        this.is_list_long_ranges_running = false;
      },
    });
  }

  listLongRanges(): void {
    this.is_list_long_ranges_running = true;
    this.long_range_service.list().subscribe({
      next: response => {
        this.default_long_ranges = this.filterDefaultLongRanges(response.data);
        this.is_list_long_ranges_running = false;
      },
      error: error => {
        this.error_service.processGenericApiError(error);
        this.is_list_long_ranges_running = false;
      },
    });
  }

  onCheck(event: any, step: number, default_long_range: LongRange): void {
    const is_checked = event.checked;

    const company_long_range = this.company_long_ranges?.find(
      clr => clr.min_amount === default_long_range.min_amount && clr.max_amount === default_long_range.max_amount,
    );

    if (company_long_range && is_checked) {
      company_long_range.allowed_steps?.push(step);
    }

    if (company_long_range && !is_checked) {
      company_long_range.allowed_steps = company_long_range.allowed_steps?.filter(s => s != step);
    }

    if (company_long_range) {
      this.company_long_ranges = this.company_long_ranges?.filter(
        clr => clr.min_amount != default_long_range.min_amount,
      );
      this.company_long_ranges?.push(company_long_range);
      this.company_long_ranges = this.orderLongRange(this.company_long_ranges || []);
    }
  }

  orderLongRange(long_ranges: LongRange[]): LongRange[] {
    return long_ranges.sort(this.compareLongRange);
  }

  compareLongRange(a: LongRange, b: LongRange): number {
    if (a.min_amount && b.min_amount && a.min_amount < b.min_amount) {
      return -1;
    }
    if (a.min_amount && b.min_amount && a.min_amount > b.min_amount) {
      return 1;
    }
    return 0;
  }

  onSaveClick(): void {
    const empty_ranges = this.company_long_ranges?.filter(clr => !clr.allowed_steps?.length);
    if (empty_ranges?.length) {
      this.dialog_service.openErrorDialog(ERROR_EMPTY_LONG_RANGES_MESSAGE);
    } else {
      console.log('Saving long ranges for company', this.company_long_ranges);
      this.setLongRanges(this.company?.company_id as string, this.company_long_ranges as LongRange[]);
    }
  }

  setLongRanges(company_id: string, long_ranges: LongRange[]): void {
    this.is_update_company_running = true;

    long_ranges = !long_ranges.length ? this.default_long_ranges || [] : long_ranges;

    this.companies_service.setLongRanges(company_id, long_ranges).subscribe({
      next: () => {
        this.is_update_company_running = false;
        this.dialog_ref.close({ success: true });
      },
      error: error => {
        this.error_service.processGenericApiError(error);
        this.is_update_company_running = false;
      },
    });
  }

  isChecked(step: number, default_long_range: LongRange): boolean {
    const company_long_range = this.company?.long_ranges?.find(
      slr => slr.min_amount === default_long_range.min_amount && slr.max_amount === default_long_range.max_amount,
    );

    if (company_long_range) {
      return company_long_range.allowed_steps?.includes(step) || false;
    }

    return false;
  }

  getLongRangeLabel(long_range: LongRange): { min_label: number; max_label: number } {
    return this.utils_service.getLongRangeLabel(long_range);
  }

  filterDefaultLongRanges(default_long_ranges: LongRange[]): LongRange[] {
    if (!this.company?.order_requirements?.is_bnpl_long_enabled) {
      // Remove 600-1000 long range with 6, 8, 10, and 12 steps if BNPL Long is not enabled for the company
      return default_long_ranges.map(dlr => {
        if (dlr.min_amount === 600 && dlr.max_amount === 1000) {
          return {
            ...dlr,
            allowed_steps: dlr.allowed_steps?.filter(step => ![6, 8, 10, 12].includes(step)),
          };
        }
        return dlr;
      });
    }
    return default_long_ranges;
  }
}
